import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PermissionState {
  loading: boolean;
  dataPermission: any | null;
  error?: string | null;
  page?: number;
  pageSize?: number;
  documentPermission?: any | null;
  folderPermission?: any | null;
  mappingPermission?: any | null;
  roleMappingLabel?: any | null;
  roleMappingLoading?: boolean;
  // Roles state
  roles?: any[] | null;
  rolesLoading?: boolean;

  // Permission user folder
  permissionUserFolder?: any | null;
  permissionUserFolderLoading?: boolean;
  
  // Role permission folder
  rolePermissionFolder?: any | null;
  rolePermissionFolderLoading?: boolean;

  // Cache permissions by folderId for quick access
  folderPermissionsCache: { [folderId: string]: any };
  filePermissionsCache: { [fileId: string]: any };
  permissionUserFile?: any;
  permissionUserFileLoading?: boolean;
}

const initialState: PermissionState = {
  loading: false,
  dataPermission: null,
  error: null,
  documentPermission: null,
  folderPermission: null,
  mappingPermission: null,
  roleMappingLabel: null,
  roleMappingLoading: false,
  folderPermissionsCache: {},
  filePermissionsCache: {},
  roles: null,
  rolesLoading: false,
  permissionUserFolder: null,
  permissionUserFolderLoading: false,
  rolePermissionFolder: null,
  rolePermissionFolderLoading: false,
  permissionUserFile: null,
  permissionUserFileLoading: false,
};

const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    getPermissionsRequest(
      state,
      action: PayloadAction<
        { page?: number; pageSize?: number; type: string } | undefined
      >
    ) {
      state.loading = true;
      state.error = null;
      state.page = action?.payload?.page;
      state.pageSize = action?.payload?.pageSize;
    },
    getPermissionsSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.dataPermission = action.payload;
      state.error = null;
    },
    getPermissionsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    // document permissions
    getDocumentUsersPermissionsRequest(
      state,
      _action: PayloadAction<{ documentId: string | number }>
    ) {
      // Just mark the request, no loading state needed
    },
    getDocumentUsersPermissionsSuccess(state, action: PayloadAction<any>) {
      state.documentPermission = action.payload;
      //console.log("action.payload", action.payload);
    },
    getDocumentUsersPermissionsFailure(state, _action: PayloadAction<string>) {
      state.documentPermission = null;
    },
    // folder permissions
    getFolderUsersPermissionsRequest(
      state,
      _action: PayloadAction<{ folderId: string | number }>
    ) {
      // Just mark the request, no loading state needed
    },
    getFolderUsersPermissionsSuccess(
      state,
      action: PayloadAction<{ folderId: string; data: any }>
    ) {
      const { folderId, data } = action.payload;
      // optional nếu vẫn cần folderPermission hiện tại
      state.folderPermission = data;
    },
    getFolderUsersPermissionsFailure(state, _action: PayloadAction<string>) {
      state.folderPermission = null;
    },
    getFileUserPermissionsRequest(
      state,
      _action: PayloadAction<{
        fileId: string | number;
        documentId: string | number;
        userId: string | number;
      }>
    ) {
      state.permissionUserFileLoading = true;
      state.error = null;
    },
    getFileUserPermissionsSuccess(
      state,
      action: PayloadAction<{
        fileId: string | number;
        data: any;
      }>
    ) {
      const { fileId, data } = action.payload;
      state.permissionUserFileLoading = false;
      state.filePermissionsCache[fileId] = data;
    },
    getFileUserPermissionsFailure(
      state,
      action: PayloadAction<string>
    ) {
      state.permissionUserFileLoading = false;
      state.error = action.payload;
    },


    // mapping permission (assign permissions to user for document)
    mappingPermissionRequest(
      _state,
      _action: PayloadAction<{
        userId: string;
        permissionIds: string[];
        labelIds?: string[];
        fileIds?: string[];
        type: string;
        documentId?: string;
      }>
    ) {
      // Just mark the request, no loading state needed
    },
    mappingPermissionSuccess(state, action: PayloadAction<any>) {
      state.mappingPermission = action.payload;
    },
    mappingPermissionFailure(state, _action: PayloadAction<string>) {
      state.mappingPermission = null;
    },
    // add permission
    addPermissionRequest(
      _state,
      _action: PayloadAction<{ id: string; permissionId: string }>
    ) {
      // Just mark the request, no loading state needed
    },
    addPermissionSuccess(_state, action: PayloadAction<any>) {
      // Optionally update state after adding permission
      //console.log("addPermissionSuccess", action.payload);
    },
    addPermissionFailure(_state, _action: PayloadAction<string>) {
      // Handle failure if needed
    },
    // delete permission
    delPermissionRequest(
      _state,
      _action: PayloadAction<{ id: string; permissionId: string }>
    ) {
      // Just mark the request, no loading state needed
    },
    delPermissionSuccess(_state, action: PayloadAction<any>) {
      // Optionally update state after deleting permission
      //console.log("delPermissionSuccess", action.payload);
    },
    delPermissionFailure(_state, _action: PayloadAction<string>) {
      // Handle failure if needed
    },
    // Cache folder permissions by folderId
    setFolderPermissionsCache(
      state,
      action: PayloadAction<{ folderId: string; permissions: any }>
    ) {
      const { folderId, permissions } = action.payload;
      state.folderPermissionsCache[folderId] = {
        ...permissions,
      };
    },
    // Get permissions from cache
    getFolderPermissionsFromCache(
      _state,
      _action: PayloadAction<{ folderId: string }>
    ) {
      // This is just a marker action, actual logic in selectors
    },
    // Clear permissions cache
    clearFolderPermissionsCache(state) {
      state.folderPermissionsCache = {};
    },
    // role mapping label (transfer ownership)
    roleMappingLabelRequest(
      state,
      _action: PayloadAction<{
        labelId_body: string;
        roleId: string;
        userId: string;
        transferedUser?: {
          name: string;
          icon: string | null;
          id: string;
          email: string;
        };
      }>
    ) {
      state.roleMappingLoading = true;
      state.error = null;
    },
    roleMappingLabelSuccess(state, action: PayloadAction<any>) {
      state.roleMappingLoading = false;
      state.roleMappingLabel = action.payload;
      state.error = null;
    },
    roleMappingLabelFailure(state, action: PayloadAction<string>) {
      state.roleMappingLoading = false;
      state.error = action.payload;
      state.roleMappingLabel = null;
    },
    // Get permission user folder
    getPermissionUserFolderRequest(
      state,
      _action: PayloadAction<{
        folderId: string | number;
        userId: string | number;
      }>
    ) {
      state.permissionUserFolderLoading = true;
      state.error = null;
    },
    getPermissionUserFolderSuccess(state, action: PayloadAction<any>) {
      const { folderId, data } = action.payload;
      state.permissionUserFolderLoading = false;
      state.permissionUserFolder = data;
      state.folderPermissionsCache[folderId] = data;
    },
    getPermissionUserFolderFailure(state, action: PayloadAction<string>) {
      state.permissionUserFolderLoading = false;
      state.error = action.payload;
      state.permissionUserFolder = null;
    },
    getRolesRequest(state) {
      state.rolesLoading = true;
      state.error = null;
    },
    getRolesSuccess(state, action: PayloadAction<any[]>) {
      state.rolesLoading = false;
      state.roles = action.payload;
      state.error = null;
    },
    getRolesFailure(state, action: PayloadAction<string>) {
      state.rolesLoading = false;
      state.error = action.payload;
      state.roles = null;
    },
    // Get role permission folder
    getRolePermissionFolderRequest(
      state,
      _action: PayloadAction<{ folderId: string | number }>
    ) {
      state.rolePermissionFolderLoading = true;
      state.error = null;
    },
    getRolePermissionFolderSuccess(state, action: PayloadAction<any>) {
      state.rolePermissionFolderLoading = false;
      state.rolePermissionFolder = action.payload;
      state.error = null;
    },
    getRolePermissionFolderFailure(state, action: PayloadAction<string>) {
      state.rolePermissionFolderLoading = false;
      state.error = action.payload;
      state.rolePermissionFolder = null;
    },
    // Revoke ownership
    revokeOwnershipRequest(
      state,
      _action: PayloadAction<{
        userId: string;
        labelId: string;
        transferedUser?: {
          name: string;
          icon: string | null;
          id: string;
          email: string;
        };
      }>
    ) {
      state.roleMappingLoading = true;
      state.error = null;
    },
    revokeOwnershipSuccess(state, _action: PayloadAction<any>) {
      state.roleMappingLoading = false;
      state.error = null;
    },
    revokeOwnershipFailure(state, action: PayloadAction<string>) {
      state.roleMappingLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  getPermissionsRequest,
  getPermissionsSuccess,
  getPermissionsFailure,
  getDocumentUsersPermissionsRequest,
  getDocumentUsersPermissionsSuccess,
  getDocumentUsersPermissionsFailure,
  getFolderUsersPermissionsRequest,
  getFolderUsersPermissionsSuccess,
  getFolderUsersPermissionsFailure,
  mappingPermissionRequest,
  mappingPermissionSuccess,
  mappingPermissionFailure,
  addPermissionRequest,
  addPermissionSuccess,
  addPermissionFailure,
  delPermissionRequest,
  delPermissionSuccess,
  delPermissionFailure,
  setFolderPermissionsCache,
  getFolderPermissionsFromCache,
  clearFolderPermissionsCache,
  roleMappingLabelRequest,
  roleMappingLabelSuccess,
  roleMappingLabelFailure,
  getPermissionUserFolderRequest,
  getPermissionUserFolderSuccess,
  getPermissionUserFolderFailure,
  getRolesRequest,
  getRolesSuccess,
  getRolesFailure,
  getRolePermissionFolderRequest,
  getRolePermissionFolderSuccess,
  getRolePermissionFolderFailure,
  revokeOwnershipRequest,
  revokeOwnershipSuccess,
  revokeOwnershipFailure,
  getFileUserPermissionsRequest,
  getFileUserPermissionsSuccess,
  getFileUserPermissionsFailure,
} = permissionSlice.actions;

// Selector to get permissions by folderId
export const selectFolderPermissions = (state: any, folderId: string) =>
  state.permission.folderPermissionsCache[folderId] || null;

// Selector to check upload permission
export const selectHasUploadPermission = (state: any) => {
  const currentFolderId = state.navigation.currentFolderId;
  const folderRootId = state.navigation.folderRootId;
  const folderPermission = state.permission.folderPermission;
  const currentWorkspace = state.navigation.currentWorkspace;

  // If current folder is root folder, always have upload permission
  if (currentFolderId === folderRootId) {
    return true;
  }

  // Only check permissions for Shared with me workspace
  if (currentWorkspace == "My Drive" || currentWorkspace == "Team Drives") {
    return true;
  }

  // Check API permissions for Shared with me workspace
  const folderPermData = folderPermission?.find(
    (item: any) => item.labelId === currentFolderId
  );

  if (folderPermData?.permissions) {
    return folderPermData.permissions.some(
      (perm: any) => perm.code === "UPLOAD"
    );
  }
  return false;
};

// Selector to check create permission
export const selectHasCreatePermission = (state: any) => {
  const currentFolderId = state.navigation.currentFolderId;
  const folderRootId = state.navigation.folderRootId;
  const folderPermission = state.permission.folderPermission;
  const currentWorkspace = state.navigation.currentWorkspace;

  // If current folder is root folder, always have create permission
  if (currentFolderId === folderRootId) {
    return true;
  }

  // Only check permissions for Shared with me workspace
  if (currentWorkspace == "My Drive" || currentWorkspace == "Team Drives") {
    return true;
  }

  if (folderPermission && Array.isArray(folderPermission)) {
    const folderPermData = folderPermission.find(
      (item: any) => item.labelId === currentFolderId
    );

    if (folderPermData && folderPermData.permissions) {
      return folderPermData.permissions.some(
        (perm: any) => perm.code === "CREATE"
      );
    }
  }
  return false;
};
export default permissionSlice.reducer;
